/**
 * dp-connector-handoff.js — ?design=-Pfad der Produktseite.
 *
 * Kommt der Kunde aus dem Kalender-Designer mit /products/<handle>?design=<id>
 * (oder ?dp_design=<id>), liest dieses Modul den Handoff-Eintrag aus dem
 * IndexedDB-Speicher (dp-handoff-store.js), prüft das Produkt, stellt die Variante
 * über window.dpBridge.preselect() ein, verifiziert die gewählten Attribute gegen
 * dpBridge.status (max. 3 Korrekturrunden), übernimmt Papier/Bindung aus entry.options
 * (aufgelöst gegen die im App-Block angebotenen Optionstexte, dpBridge.listOptionValues) und legt die Druckdatei per
 * dpBridge.prepareHandoff() in den Konfigurator. Danach zeigt die Brücke ihren
 * Bestätigungsschritt — abgesendet wird ausschließlich durch den Klick des Kunden.
 *
 * Sicherheitsregel: Dieses Modul löst NIE eine Bestellung aus. Es kennt weder
 * den Bestell-Button noch finalizeVerticalOrder; es ruft nur die Bridge-API.
 *
 * Status im Speicher: prepareHandoff ok → 'staged'. 'submitted' wird gesetzt,
 * sobald dpBridge.status.handoff.phase === 'submitted' beobachtet wird (die Brücke
 * setzt das in confirmSubmit; ein eigenes Ereignis gibt es dort noch nicht — siehe
 * Antwort/Doku „offene Haken"). Der Eintrag wird hier nie gelöscht.
 *
 * Fehler landen als Klartext (Du-Form) in einem Hinweisfeld über dem Konfigurator
 * mit „Druckdatei selbst herunterladen" und „Zurück zum Designer".
 * Bricht der Kunde den Bestätigungsschritt ab (Ereignis 'dp:bridge-cancelled' der
 * Brücke), erklärt dieselbe Karte den Rückweg („Deine Druckdatei liegt schon im
 * Konfigurator …") und bietet „Erneut prüfen lassen" – das ruft prepareHandoff()
 * noch einmal (die App setzt uploadedFiles vor jedem Einlegen zurück, app-new.js:1908).
 * Ohne ?design=/?dp_design= bricht das Modul sofort ab (keine Kosten).
 */

import { renderFlowSteps } from './dp-flow-steps.js?v=6c1534790e';

export const VERSION = '0.1.0';
export const DESIGN_PARAMS = Object.freeze(['design', 'dp_design']);
export const DESIGN_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
export const MAX_VERIFY_ROUNDS = 3;

export const MSG = Object.freeze({
  alreadySubmitted: 'Diese Druckdatei wurde bereits an die Druckerei übertragen. Du findest den Auftrag im Fenster „Upload-Status anzeigen“. Falls du sie erneut übertragen möchtest, nutze den Link unten.',
  resubmit: 'Erneut übertragen',
  busy: 'Dein Kalender ist fertig gestaltet. Wir übernehmen ihn jetzt ins Bestellformular – gleich kannst du die Druckdatei prüfen lassen.',
  notFound: 'Wir haben deinen Entwurf in diesem Browser nicht gefunden. Öffne den Kalender-Designer und erzeuge die Druckdatei noch einmal.',
  expired: 'Deine Druckdatei ist abgelaufen – wir bewahren sie sieben Tage auf. Bitte erzeuge sie im Kalender-Designer noch einmal.',
  wrongPage: 'Dein Kalender gehört zu einem anderen Produkt. Auf der passenden Produktseite geht es direkt weiter.',
  noBridge: 'Das Bestellformular ließ sich nicht verbinden. Lade die Seite neu oder lade die Druckdatei unten selbst hoch.',
  notReady: 'Das Bestellformular hat nicht geantwortet – wir haben gut zwei Minuten gewartet. Bitte lade die Seite neu; deine Druckdatei bleibt gespeichert.',
  formatUnknown: 'Wir konnten das Format deines Kalenders diesem Produkt nicht zuordnen.',
  noFile: 'Zu deinem Entwurf liegt keine Druckdatei mehr vor. Bitte erzeuge sie im Kalender-Designer noch einmal.',
  prepareFailed: 'Die Druckdatei konnte nicht ins Bestellformular übernommen werden.',
  mismatch: (list) => `Das Bestellformular hat ein anderes Format eingestellt (${list.map((m) => `${m.attribute}: ${m.actual ?? 'fehlt'} statt ${m.wanted}`).join('; ')}). Bitte prüfe die Auswahl unten oder lade die Druckdatei selbst hoch.`,
  unexpected: (m) => `Unerwarteter Fehler: ${m}`,
  optionsUnavailable: 'Papier/Bindung: Standard, deine Wahl ist für dieses Format nicht verfügbar',
  cancelled: 'Deine Druckdatei liegt schon im Konfigurator – du kannst unten weitermachen oder oben erneut prüfen lassen.',
  recheck: 'Erneut prüfen lassen',
  download: 'Druckdatei selbst herunterladen',
  backToDesigner: 'Zurück zum Designer',
  toProduct: 'Zur passenden Produktseite',
});

// ── Reine Helfer ────────────────────────────────────────────────────────────

/** ?design=<id> vor ?dp_design=<id>; ungültige IDs zählen als nicht vorhanden. */
export function readDesignParam(search) {
  const params = new URLSearchParams(String(search ?? ''));
  for (const param of DESIGN_PARAMS) {
    const value = params.get(param);
    if (value != null && DESIGN_ID_PATTERN.test(value)) return { designId: value, param };
  }
  return null;
}

/** "/de/collections/x/products/foto-kalender?y" -> "foto-kalender" */
export function pageHandleFromPath(pathname) {
  const hit = /(?:^|\/)products\/([^/?#]+)/.exec(String(pathname ?? ''));
  return hit ? decodeURIComponent(hit[1]) : null;
}

export function productPageUrl(handle, designId, param = 'design') {
  return `/products/${encodeURIComponent(handle)}?${param}=${encodeURIComponent(designId)}`;
}

export function designerUrl(base, { dpProductId = null, formatKey = null, designId = null } = {}) {
  const params = new URLSearchParams();
  if (dpProductId) params.set('product', String(dpProductId));
  if (formatKey) params.set('fmt', String(formatKey));
  if (designId) params.set('design', String(designId));
  const query = params.toString();
  return query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base;
}

const normalize = (v) => String(v ?? '').replace(/[®™]/g, '').replace(/\u00a0/g, ' ').replace(/×/g, 'x').replace(/\s+/g, ' ').trim().toLowerCase();
const stripCert = (v) => v.replace(/(\s+(pefc|fsc))+$/, '').trim();
const normName = (v) => normalize(v).replace(/[-_]+/g, ' ');

/** Attribute der gewählten App-Variante gegen die Zielattribute (Zertifikats-Endung tolerant). */
/**
 * Weiche Attribute: Zubehör, das FLYERALARM nicht für jede Papier/Bindungs-Kombination anbietet
 * (Befund 2026-09-16: Tischkalender, 250 g glänzend + silber → nur „ohne Abdeckfolie"). Weicht nur
 * so ein Attribut ab, ist die Variante trotzdem gültig – der Kunde bekommt einen Hinweis statt einer
 * Sackgasse. Format, Ausführung, Blattzahl, Material und Bindungsfarbe bleiben hart.
 */
export const SOFT_ATTRS = Object.freeze(['Weiterverarbeitung', 'Veredelung']);

export function attrMismatches(selectedAttrs, wanted, { soft = SOFT_ATTRS } = {}) {
  const selected = Object.entries(selectedAttrs ?? {}).map(([name, value]) => [normName(name), value]);
  const out = [];
  const softNames = new Set((soft ?? []).map(normName));
  for (const [name, value] of Object.entries(wanted ?? {})) {
    if (value == null || value === '') continue;
    const hit = selected.find(([n]) => n === normName(name));
    const actual = hit ? hit[1] : null;
    const same = hit && (normalize(actual) === normalize(value) || stripCert(normalize(actual)) === stripCert(normalize(value)));
    if (!same) out.push({ attribute: name, wanted: value, actual, soft: softNames.has(normName(name)) });
  }
  return out;
}

/** Hinweistext für weiche Abweichungen („Abdeckfolie: ohne statt mit – für diese Kombination nicht verfügbar"). */
export function softMismatchNote(list) {
  const items = (list ?? []).filter((m) => m.soft).map((m) => {
    if (/abdeckfolie/i.test(String(m.wanted)) && /ohne/i.test(String(m.actual ?? ''))) return 'Die Abdeckfolie gibt es für diese Papier/Bindungs-Kombination nicht – dein Kalender kommt ohne Abdeckfolie.';
    return `${m.attribute}: ${m.actual ?? 'nicht verfügbar'} statt ${m.wanted} – für diese Kombination nicht anders verfügbar.`;
  });
  return items.join(' ');
}

/** Zielattribute: Format-Map (products[dpId].attrs) als Basis, entry.attrs (z. B. Papier/Bindung) darüber. */
export function resolveHandoffAttrs({ formatMap = null, formatKey = null, dpProductId = null, entryAttrs = null } = {}) {
  const entry = formatMap?.formats?.[formatKey] ?? null;
  const product = dpProductId != null ? entry?.products?.[String(dpProductId)] ?? null : null;
  const base = product?.attrs ?? null;
  const attrs = { ...(base ?? {}), ...(entryAttrs && typeof entryAttrs === 'object' ? entryAttrs : {}) };
  if (!Object.keys(attrs).length) return null;
  return { attrs, label: entry?.label ?? null, dpProductId: dpProductId != null ? String(dpProductId) : null, productHandle: product?.productHandle ?? formatMap?.productHandles?.[String(dpProductId)] ?? null, source: base ? 'format-map' : 'entry' };
}

// ── Papier/Bindung aus dem Designer (entry.options) ─────────────────────────
/** Schnittstelle zum Designer: entry.options = { paper, binding }; Attributnamen im App-Block. */
export const OPTION_ATTRS = Object.freeze({ paper: 'Material', binding: 'Farbe der Wire-O-Bindung' });
export const DEFAULT_OPTIONS = Object.freeze({ paper: 'matt-170', binding: 'schwarz' });
const OPTION_RULES = {
  'matt-170': (t) => t.includes('170') && t.includes('matt'),
  'glanz-170': (t) => t.includes('170') && /gl[aä]nz/.test(t),
  'glanz-250': (t) => t.includes('250'),
  schwarz: (t) => t.includes('schwarz'),
  silber: (t) => t.includes('silber'),
  weiss: (t) => t.includes('weiss'),
};
const optionKey = (key) => normalize(key).replace(/ß/g, 'ss');

/**
 * Sucht in den im App-Block angebotenen Optionstexten den Eintrag zum Designer-Schlüssel
 * (paper: 'matt-170' | 'glanz-170' | 'glanz-250'; binding: 'schwarz' | 'silber' | 'weiss'/'weiß').
 * Liefert den Originaltext der ersten passenden Option, sonst null — nie einen festen String.
 */
export function matchOptionValue(values, key) {
  const rule = OPTION_RULES[optionKey(key)];
  if (!rule || !Array.isArray(values)) return null;
  for (const value of values) {
    const text = normalize(value).replace(/ß/g, 'ss');
    if (text && rule(text)) return String(value).replace(/\s+/g, ' ').trim();
  }
  return null;
}

/**
 * Überschreibt Material/Bindung in den Zielattributen mit der im App-Block angebotenen Option zum
 * Designer-Wunsch. listOptionValues(label) liefert die <option>-Texte des passenden Selects.
 * Ohne Treffer bleibt das Attribut unverändert (Standard der Format-Map) und der Schlüssel landet in
 * `unavailable`; der Standardwunsch selbst zählt dabei nicht als „nicht verfügbar".
 */
export function applyDesignerOptions({ attrs = {}, options = null, listOptionValues = null } = {}) {
  const wish = { ...DEFAULT_OPTIONS, ...(options && typeof options === 'object' ? options : {}) };
  const out = { ...attrs };
  const applied = {};
  const unavailable = [];
  for (const [kind, label] of Object.entries(OPTION_ATTRS)) {
    const key = wish[kind];
    if (!key) continue;
    let values = [];
    try { values = listOptionValues?.(label) ?? []; } catch { values = []; }
    const hit = matchOptionValue(values, key);
    if (hit) { out[label] = hit; applied[kind] = hit; }
    else { applied[kind] = null; if (optionKey(key) !== optionKey(DEFAULT_OPTIONS[kind])) unavailable.push(kind); }
  }
  return { attrs: out, applied, unavailable, wish };
}

/** PDF → ZIP (dp-zip-store.js, relativ zum Modul geladen); ohne File-Konstruktor oder bei Fehlern bleibt die PDF. */
export async function defaultWrapFile(file) {
  if (!(file instanceof Blob)) return file;
  const isPdf = /pdf/i.test(file.type || '') || /\.pdf$/i.test(file.name || '');
  if (!isPdf) return file;
  // 1. Wahl: Einzelseiten-Archiv (0001.pdf … NNNN.pdf) – dafür pdf-lib nachladen (data-dp-pdf-lib am Script-Tag).
  try {
    const PDFLib = await ensurePdfLib();
    if (PDFLib) {
      const split = await import('./dp-print-split.js?v=65c33318f4');
      const archive = await split.buildUploadArchive(file, { PDFLib });
      return new File([archive.bytes], archive.fileName, { type: 'application/zip' });
    }
  } catch (error) {
    console.warn('[dp-connector-handoff] Einzelseiten-Archiv fehlgeschlagen – Rückfall auf ZIP mit Mehrseiten-PDF.', error);
  }
  // 2. Rückfall: die Mehrseiten-PDF als 0001.pdf in einem ZIP.
  try {
    const mod = await import('./dp-zip-store.js?v=2996acf794');
    return await mod.zipFileFromPdf(file, '0001.pdf');
  } catch (error) {
    console.warn('[dp-connector-handoff] ZIP-Verpackung fehlgeschlagen – PDF wird unverändert übergeben.', error);
    return file;
  }
}

/** pdf-lib (UMD, window.PDFLib) bei Bedarf über die am Script-Tag hinterlegte Asset-URL laden. */
export async function ensurePdfLib(win = globalThis) {
  if (win.PDFLib?.PDFDocument) return win.PDFLib;
  const doc = win.document;
  const url = doc?.querySelector?.('script[data-dp-pdf-lib]')?.dataset?.dpPdfLib;
  if (!doc || !url) return null;
  await new Promise((resolve, reject) => {
    const s = doc.createElement('script');
    s.src = url; s.async = true;
    s.onload = resolve; s.onerror = () => reject(new Error('pdf-lib konnte nicht geladen werden.'));
    doc.head.appendChild(s);
  });
  return win.PDFLib?.PDFDocument ? win.PDFLib : null;
}

export const readStatus = (bridge) => (typeof bridge?.status === 'function' ? bridge.status() : bridge?.status) ?? null;

/** Abbruch-Ereignis der Brücke (dp-connector-bridge.js CANCEL_EVENT) abonnieren; gibt eine Abmeldefunktion zurück. */
export const CANCEL_EVENT = 'dp:bridge-cancelled';
export function subscribeCancel(handler, doc = globalThis.document) {
  if (!doc?.addEventListener) return null;
  const listener = (event) => handler(event?.detail ?? {});
  doc.addEventListener(CANCEL_EVENT, listener);
  return () => doc.removeEventListener?.(CANCEL_EVENT, listener);
}

/** Meldet einmalig, sobald die Brücke die Bestätigung des Kunden verarbeitet hat (status.handoff.phase === 'submitted'). */
export function watchSubmitted(bridge, onSubmitted, { intervalMs = 300, timeoutMs = 15 * 60 * 1000, timers = globalThis } = {}) {
  const startedAt = Date.now();
  const timer = timers.setInterval(() => {
    const handoff = readStatus(bridge)?.handoff;
    if (handoff?.phase === 'submitted') {
      timers.clearInterval(timer);
      onSubmitted({ submittedAt: handoff.submittedAt ?? Date.now(), quantity: Number(handoff.submittedQuantity) || 1, totalPrice: handoff.submittedTotalPrice ?? null });
    } else if (Date.now() - startedAt > timeoutMs) {
      timers.clearInterval(timer);
    }
  }, intervalMs);
  return () => timers.clearInterval(timer);
}

// ── Ablauf (testbar mit Fake-Bridge, Fake-Store, Fake-UI) ───────────────────

export async function runHandoff({
  designId, param = 'design', store, ui, bridge = null, waitForBridge = null, formatMap = null, loadFormatMap = null,
  pageHandle = null, pageProductId = null, designerBase = '/pages/designer', maxRounds = MAX_VERIFY_ROUNDS, watch = watchSubmitted,
  wrapFile = defaultWrapFile, allowResubmit = false, onCancel = null,
}) {
  const links = (entry) => [{ label: MSG.backToDesigner, href: designerUrl(designerBase, { dpProductId: entry?.dpProductId, formatKey: entry?.formatKey, designId }) }];
  const download = (entry) => (entry?.blob instanceof Blob ? { blob: entry.blob, fileName: entry.fileName || 'kalender.pdf' } : null);
  let entry = null;
  const fail = async (step, message, detail = null) => {
    if (entry) {
      try { await store.setStatus(designId, entry.status, { attempts: (entry.attempts ?? 0) + 1, lastError: { step, message, at: Date.now() } }); } catch { /* Speicherfehler blockieren den Hinweis nicht */ }
    }
    ui.error({ message, download: download(entry), links: links(entry) });
    return { ok: false, step, message, detail };
  };
  try {
    entry = await store.get(designId);
    if (!entry) return fail('entry', MSG.notFound);
    if (store.isExpired?.(entry)) return fail('expired', MSG.expired);
    // Bereits abgeschickte Entwürfe nicht bei jedem Seitenaufruf erneut in den Konfigurator legen —
    // die App legt sonst je Aufruf einen weiteren Hold-Auftrag an (Befund 2026-09-14).
    if (entry.status === 'submitted' && !allowResubmit) {
      ui.error({ message: MSG.alreadySubmitted, download: download(entry), links: [{ label: MSG.resubmit, href: productPageUrl(pageHandle || entry.productHandle, designId, param) + '&resubmit=1' }, ...links(entry)] });
      return { ok: false, step: 'already-submitted' };
    }
    if (entry.productHandle && pageHandle && entry.productHandle !== pageHandle) {
      const href = productPageUrl(entry.productHandle, designId, param);
      ui.redirect({ message: MSG.wrongPage, href, label: MSG.toProduct, links: links(entry) });
      return { ok: false, step: 'product-page', href };
    }
    ui.busy(MSG.busy);
    bridge = bridge ?? (await waitForBridge?.()) ?? null;
    if (!bridge) return fail('bridge', MSG.noBridge);
    const ready = await bridge.ready();
    if (!ready?.ok) return fail('ready', MSG.notReady, ready?.detail ?? null);
    const productId = pageProductId ?? readStatus(bridge)?.productId ?? null;
    const dpProductId = entry.dpProductId ?? productId;
    formatMap = formatMap ?? (await loadFormatMap?.()) ?? null;
    const target = resolveHandoffAttrs({ formatMap, formatKey: entry.formatKey, dpProductId, entryAttrs: entry.attrs });
    if (productId && dpProductId && String(productId) !== String(dpProductId)) {
      const handle = target?.productHandle ?? formatMap?.productHandles?.[String(dpProductId)] ?? null;
      if (handle) {
        const href = productPageUrl(handle, designId, param);
        ui.redirect({ message: MSG.wrongPage, href, label: MSG.toProduct, links: links(entry) });
        return { ok: false, step: 'product-page', href };
      }
      return fail('product', MSG.wrongPage, { pageProductId: productId, dpProductId });
    }
    if (!target) return fail('format', MSG.formatUnknown, { formatKey: entry.formatKey, dpProductId });
    let mismatches = [];
    let rounds = 0;
    let designerOptions = null;
    for (rounds = 1; rounds <= maxRounds; rounds += 1) {
      const result = await bridge.preselect(target.attrs, { key: entry.formatKey, dpProductId: target.dpProductId, source: 'handoff' });
      // Papier/Bindung erst nach der ersten Runde auflösen: Die App filtert die <option>-Listen nach der
      // zuletzt geänderten Auswahl, erst jetzt zeigt das Select die zum Format passenden Materialien.
      if (rounds === 1 && typeof bridge.listOptionValues === 'function') {
        designerOptions = applyDesignerOptions({ attrs: target.attrs, options: entry.options, listOptionValues: bridge.listOptionValues });
        target.attrs = designerOptions.attrs;
      }
      const last = readStatus(bridge)?.preselect?.last ?? result;
      const selected = last?.variant?.detail?.selectedAttrs ?? null;
      mismatches = selected ? attrMismatches(selected, target.attrs) : [{ attribute: 'Variante', wanted: target.label ?? entry.formatKey, actual: last?.reason ?? null }];
      if (!mismatches.some((m) => !m.soft)) break; // nur weiche Abweichungen → Variante gilt
    }
    const hard = mismatches.filter((m) => !m.soft);
    if (hard.length) return fail('verify', MSG.mismatch(hard), { rounds: Math.min(rounds, maxRounds), mismatches });
    const softNote = softMismatchNote(mismatches);
    // Weiche Abweichungen in die Zielattribute übernehmen: prepareHandoff() stellt die Variante noch
    // einmal ein und würde sonst an „mit Abdeckfolie" scheitern, obwohl die App nur „ohne" anbietet.
    for (const m of mismatches) {
      if (!m.soft) continue;
      if (m.actual != null) { target.attrs = { ...target.attrs, [m.attribute]: m.actual }; continue; }
      // Die App bietet dieses Merkmal für die Kombination GAR NICHT an (kein Select, actual === null):
      // dann darf der Wunsch auch nicht in den Zielattributen stehen, sonst scheitert prepareHandoff mit
      // App-Rohtext (Druckprüfung 2026-09-22 D-4).
      const { [m.attribute]: _entfaellt, ...rest } = target.attrs;
      target.attrs = rest;
    }
    if (!(entry.blob instanceof Blob)) return fail('file', MSG.noFile);
    // Der dp-digital-Server nimmt Druckdaten nur als ZIP an („Upload supports only ZIP files",
    // Live-Befund 2026-09-14) — die PDF wird deshalb in ein store-ZIP verpackt (dp-zip-store.js).
    // Bevorzugt das im Designer gebaute Upload-Archiv (ZIP mit 0001.pdf … 0013.pdf, dp-print-split.js);
    // sonst Rückfall: die Mehrseiten-PDF als 0001.pdf in ein ZIP verpacken.
    let file;
    if (entry.uploadBlob instanceof Blob && entry.uploadBlob.size > 0) {
      file = new File([entry.uploadBlob], entry.uploadFileName || 'druckdaten.zip', { type: 'application/zip' });
    } else {
      const pdfFile = new File([entry.blob], '0001.pdf', { type: entry.mimeType || 'application/pdf' });
      file = await wrapFile(pdfFile);
    }
    const fileName = file.name;
    const notes = [...(designerOptions?.unavailable?.length ? [MSG.optionsUnavailable] : []), ...(softNote ? [softNote] : [])];
    // Wunschmenge aus der Staffel-Karte des Designers (entry.wantedQuantity, 2/3): die Brücke wendet sie nach dem
    // Bestätigungsschritt über den Kachel-Weg an – nur, wenn die App die Menge anbietet; sonst Menge 1 mit Hinweis.
    const wantedQuantity = Number.isInteger(entry.wantedQuantity) && entry.wantedQuantity > 1 ? entry.wantedQuantity : null;
    // Versandart des Kunden an die Brücke: sie stellt damit die Variante im Konfigurator ein.
    const speed = entry.speed === 'express' ? 'express' : 'standard';
    const prepare = () => bridge.prepareHandoff({ file, fileName, orderName: entry.orderName || undefined, designId, formatKey: entry.formatKey, attrs: target.attrs, dpProductId: target.dpProductId, label: target.label, notes, wantedQuantity, speed });
    const prepared = await prepare();
    if (!prepared?.ok) return fail('prepare', prepared?.message || MSG.prepareFailed, prepared ?? null);
    ui.close();
    await store.setStatus(designId, 'staged', { attempts: (entry.attempts ?? 0) + 1, lastError: null, preflight: prepared.preflight ?? entry.preflight ?? null });
    const stop = watch?.(bridge, ({ submittedAt, quantity, totalPrice }) => store.setStatus(designId, 'submitted', { preflight: prepared.preflight ?? null, lastError: null, submittedAt, quantity: quantity ?? 1, totalPrice: totalPrice ?? null }).catch(() => null));
    // Abbruch im Dialog (F-17): Rückweg erklären und den Dialog auf Wunsch erneut vorbereiten (Menge wieder 1 bzw. der Designer-Wunsch).
    const reopen = async () => {
      ui.busy(MSG.busy);
      const again = await prepare();
      if (again?.ok) ui.close();
      else ui.error({ message: again?.message || MSG.prepareFailed, download: download(entry), links: links(entry) });
      return again;
    };
    const stopCancel = typeof onCancel === 'function' && typeof ui.resume === 'function'
      ? onCancel((detail) => { if (!detail?.designId || detail.designId === designId) ui.resume({ message: MSG.cancelled, label: MSG.recheck, links: links(entry), onReopen: reopen }); })
      : null;
    return { ok: true, step: 'staged', rounds: Math.min(rounds, maxRounds), live: Boolean(prepared.live), stopWatching: stop ?? null, stopCancelWatching: stopCancel ?? null };
  } catch (error) {
    return fail('unexpected', MSG.unexpected(error?.message ?? String(error)), { error: String(error?.stack ?? error) });
  }
}

// ── Hinweisfeld (nur Browser) ───────────────────────────────────────────────
const CSS = `
.dp-handoff-notice{max-width:var(--page-width,1400px);margin:var(--dp-space-md,16px) auto;padding:var(--dp-space-md,16px) var(--dp-space-lg,24px);border-radius:var(--dp-radius-md,12px);background:var(--dp-cream,#F7F4EE);border:1px solid var(--dp-border-soft,#d9d3c4);color:var(--dp-ink,#1E241C);font-size:var(--dp-font-body,1rem);line-height:1.5;display:grid;gap:var(--dp-space-sm,12px)}
.dp-handoff-notice[hidden]{display:none}
.dp-handoff-notice--error{border-left:3px solid var(--dp-coral,#E4714C)}
.dp-handoff-notice__text{margin:0;font-weight:600}
.dp-handoff-notice__flow{margin:0 0 4px}
.dp-handoff-notice__spinner{flex:none;display:inline-block;width:1rem;height:1rem;margin-right:.5rem;vertical-align:-2px;border-radius:50%;border:2px solid var(--dp-border-soft,#d9d3c4);border-top-color:var(--dp-green,#4A5B3A);animation:dp-handoff-spin .9s linear infinite}
@keyframes dp-handoff-spin{to{transform:rotate(360deg)}}
.dp-handoff-notice__actions{display:flex;flex-wrap:wrap;gap:var(--dp-space-sm,12px)}
.dp-handoff-notice__btn{display:inline-block;border:1px solid var(--dp-border,#8E8571);border-radius:var(--dp-radius-pill,100px);padding:.6rem 1.1rem;font:inherit;font-weight:600;color:var(--dp-ink,#1E241C);text-decoration:none;background:transparent}
.dp-handoff-notice__btn--primary{background:var(--dp-green,#4A5B3A);color:var(--dp-cream,#F7F4EE);border-color:transparent}
.dp-handoff-notice__btn:focus-visible{outline:2px solid var(--dp-green,#4A5B3A);outline-offset:3px}
`;

function createNoticeUi() {
  let node = null;
  let objectUrl = null;
  const ensure = () => {
    if (node) return node;
    if (!document.getElementById('dp-handoff-style')) {
      const style = document.createElement('style');
      style.id = 'dp-handoff-style';
      style.textContent = CSS;
      document.head.appendChild(style);
    }
    node = document.createElement('section');
    node.className = 'dp-handoff-notice';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    // Oben auf der Seite, nicht im (eingeklappten) Konfigurator-Band: Der Kunde kommt aus dem Designer
    // und soll sofort sehen, dass es weitergeht (Schritt-Anzeige + Text), bevor der Dialog aufgeht.
    const main = document.querySelector('.dp-product-main');
    if (main) main.insertBefore(node, main.firstChild);
    else (document.querySelector('#dp-connector') ?? document.querySelector('[data-dp-bridge]')?.parentElement ?? document.body).prepend(node);
    return node;
  };
  const link = (label, href, primary = false, downloadName = null) => {
    const a = document.createElement('a');
    a.className = `dp-handoff-notice__btn${primary ? ' dp-handoff-notice__btn--primary' : ''}`;
    a.href = href;
    a.textContent = label;
    if (downloadName) a.download = downloadName;
    return a;
  };
  const button = (label, onClick) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'dp-handoff-notice__btn dp-handoff-notice__btn--primary';
    b.textContent = label;
    b.addEventListener('click', () => { b.disabled = true; Promise.resolve(onClick?.()).finally(() => { b.disabled = false; }); });
    return b;
  };
  const render = (message, actions, error, { busy = false } = {}) => {
    const el = ensure();
    el.classList.toggle('dp-handoff-notice--error', error);
    el.hidden = false;
    const flow = document.createElement('div');
    flow.className = 'dp-handoff-notice__flow';
    flow.innerHTML = renderFlowSteps({ variant: 'design', active: 'check', compact: true });
    const text = document.createElement('p');
    text.className = 'dp-handoff-notice__text';
    if (busy) text.append(Object.assign(document.createElement('span'), { className: 'dp-handoff-notice__spinner' }));
    text.append(document.createTextNode(message));
    const row = document.createElement('div');
    row.className = 'dp-handoff-notice__actions';
    row.append(...actions);
    el.replaceChildren(flow, text, row);
    if (error) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const revoke = () => { if (objectUrl) URL.revokeObjectURL(objectUrl); objectUrl = null; };
  return {
    busy: (message) => render(message, [], false, { busy: true }),
    error: ({ message, download = null, links = [] }) => {
      revoke();
      const actions = [];
      if (download) { objectUrl = URL.createObjectURL(download.blob); actions.push(link(MSG.download, objectUrl, true, download.fileName)); }
      for (const l of links) actions.push(link(l.label, l.href));
      render(message, actions, true);
    },
    redirect: ({ message, href, label, links = [] }) => render(message, [link(label, href, true), ...links.map((l) => link(l.label, l.href))], false),
    /** Nach Abbruch im Dialog: Rückweg erklären, „Erneut prüfen lassen" öffnet den Dialog wieder. */
    resume: ({ message, label, onReopen, links = [] }) => {
      const reopen = button(label, onReopen);
      render(message, [reopen, ...links.map((l) => link(l.label, l.href))], false);
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Fokus nach Abbrechen/Escape auf den Rückweg statt auf <body> (Kaufweg-QA 2026-09-17).
      setTimeout(() => { if (reopen?.isConnected) reopen.focus({ preventScroll: true }); }, 0);
    },
    close: () => { revoke(); if (node) node.hidden = true; },
  };
}

// ── Start ───────────────────────────────────────────────────────────────────
function waitForBridgeGlobal(timeoutMs = 10000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => (window.dpBridge?.ready ? resolve(window.dpBridge) : Date.now() - startedAt > timeoutMs ? resolve(null) : setTimeout(tick, 100));
    tick();
  });
}

async function boot() {
  const found = readDesignParam(window.location.search);
  if (!found) return;
  const script = document.querySelector('script[data-dp-module-store]');
  const wrapper = document.querySelector('[data-dp-bridge]');
  const storeUrl = new URL(script?.dataset.dpModuleStore || './dp-handoff-store.js', script?.dataset.dpModuleStore ? window.location.href : import.meta.url).href;
  const ui = createNoticeUi();
  let store;
  try {
    store = (await import(storeUrl)).getHandoffStore();
  } catch (error) {
    ui.error({ message: MSG.unexpected(error?.message ?? String(error)), links: [{ label: MSG.backToDesigner, href: '/pages/designer' }] });
    return;
  }
  const designerBase = document.querySelector('a[href*="/pages/designer"]')?.getAttribute('href')?.split('?')[0] || '/pages/designer';
  const mapUrl = wrapper?.dataset.dpFormatMapUrl;
  const loadFormatMap = () => (mapUrl ? fetch(mapUrl, { credentials: 'omit' }).then((r) => (r.ok ? r.json() : null)).catch(() => null) : Promise.resolve(null));
  const result = await runHandoff({
    designId: found.designId, param: found.param, store, ui, waitForBridge: waitForBridgeGlobal, loadFormatMap,
    pageHandle: pageHandleFromPath(window.location.pathname), pageProductId: document.querySelector('.dp-main')?.dataset.productId ?? null, designerBase,
    allowResubmit: new URLSearchParams(window.location.search).get('resubmit') === '1',
    onCancel: subscribeCancel,
  });
  window.dpHandoff = { version: VERSION, designId: found.designId, result };
  store.prune().catch(() => null);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
